import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import {
  resolveProcessFolderConfig,
  type ProcessFolderConfig,
} from './processFolderConfig';

interface FolderResponse {
  folder?: {
    id: string;
    domain?: string | null;
    metadata?: Record<string, unknown> | null;
  };
}

export interface FolderProcessConfigQuery {
  /** Folder kind, when known. */
  domain: string | null;
  /** Resolved configuration — empty defaults when nothing is stored. */
  config: ProcessFolderConfig;
  /** True when the folder has anything stored under `processFolderConfig`. */
  hasConfig: boolean;
}

const EMPTY_RESULT: FolderProcessConfigQuery = {
  domain: null,
  config: resolveProcessFolderConfig(null),
  hasConfig: false,
};

/**
 * Fetch the folder-level Process configuration. Cached by folder id;
 * callers should invalidate `['process-folder-config', folderId]` after
 * mutations.
 */
export function useProcessFolderConfig(folderId: string | null | undefined) {
  const authedFetch = useAuthenticatedFetch();
  return useQuery<FolderProcessConfigQuery>({
    queryKey: ['process-folder-config', folderId],
    enabled: Boolean(folderId),
    staleTime: 30_000,
    queryFn: async (): Promise<FolderProcessConfigQuery> => {
      if (!folderId) return EMPTY_RESULT;
      const res = await authedFetch(`/api/folders/${encodeURIComponent(folderId)}`);
      if (!res.ok) return EMPTY_RESULT;
      const body = (await res.json().catch(() => null)) as FolderResponse | null;
      const folder = body?.folder;
      const metadata = (folder?.metadata ?? null) as Record<string, unknown> | null;
      const hasConfig = Boolean(
        metadata && (metadata as Record<string, unknown>).processFolderConfig,
      );
      return {
        domain: folder?.domain ?? null,
        config: resolveProcessFolderConfig(metadata),
        hasConfig,
      };
    },
  });
}
